import { Modal, notification } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/es/upload';
import { ReactNode, useState } from 'react';
import { API_BASE_URL } from '../config';
import { Callout, CalloutTone, UploadDropzone } from './ui';

export interface ImportModalConfig {
  title: string;
  description: string;
  /** Relative path (resolved against API_BASE_URL) or absolute URL. */
  action: string;
  successMessage: string;
  errorMessage: string;
  callout?: { tone: CalloutTone; title?: string; body: ReactNode };
  /** Parse the upload response and return a list of skipped/unknown domains. */
  parseSkipped?: (response: unknown) => string[];
  /** Invoked on successful upload, after notification fires. */
  onUploaded?: (response: unknown) => void;
}

interface Props {
  config: ImportModalConfig;
  onClose: () => void;
}

function resolveAction(action: string): string {
  if (action.startsWith('http://') || action.startsWith('https://')) return action;
  return API_BASE_URL + action;
}

export function ImportModal({ config, onClose }: Props) {
  const [skipped, setSkipped] = useState<string[] | null>(null);

  function onChange(info: UploadChangeParam<UploadFile<any>>) {
    if (info.file.status === 'done') {
      try {
        if (config.parseSkipped) {
          setSkipped(config.parseSkipped(info.file.response));
        }
        notification.success({ message: config.successMessage });
        config.onUploaded?.(info.file.response);
      } catch {
        notification.error({ message: 'Failed to process response.' });
      }
    } else if (info.file.status === 'error') {
      notification.error({ message: config.errorMessage });
    }
  }

  function onDrop() {
    setSkipped(null);
  }

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      width={560}
      closable={false}
      destroyOnClose
      styles={{
        content: { padding: 0, background: 'rgb(var(--color-panel) / 1)', borderRadius: 10, overflow: 'hidden' },
        body: { padding: 0 },
      }}
    >
      <div className="px-6 pt-5 pb-4 border-b border-border bg-panel flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-text-3 font-semibold mb-1">Import</div>
          <div className="text-text text-[17px] font-semibold leading-tight">{config.title}</div>
          <div className="text-text-3 text-[12.5px] mt-1">{config.description}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="w-[28px] h-[28px] rounded-md border border-border bg-panel flex items-center justify-center cursor-pointer text-text-3 hover:bg-bg-elevated hover:text-text flex-shrink-0"
        >
          ✕
        </button>
      </div>
      <div className="px-6 py-5 bg-bg flex flex-col gap-4">
        {config.callout && (
          <Callout tone={config.callout.tone} title={config.callout.title}>
            {config.callout.body}
          </Callout>
        )}
        <UploadDropzone action={resolveAction(config.action)} onChange={onChange} onDrop={onDrop} />
        {skipped && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-3 font-semibold mb-1.5">
              Domains Skipped ({skipped.length})
            </div>
            {skipped.length === 0 ? (
              <p className="text-text-3 text-[12.5px]">None</p>
            ) : (
              <ul className="font-mono text-[11.5px] text-text-2 bg-bg-elevated border border-border rounded-md p-3 max-h-[260px] overflow-auto">
                {skipped.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
